/**
 * Fermat Cookie Utility
 * 
 * Handles reading and processing of Fermat cart cookies for seamless
 * cart handoff from Fermat subdomain (on.ritual.com) to main Ritual website.
 */

/* eslint-disable no-console */

import { getCookie } from './cookies';

// Fermat checkout item interface
export interface FermatCheckoutItem {
  variant_id: string;
  product_id: string;
  quantity: number;
  unit_price: string;
  external_price_id: string;
  subscription_id?: string;
}

// Fermat checkout interface
export interface FermatCheckout {
  id: string;
  items: FermatCheckoutItem[];
  subtotal: string;
  total: string;
  currency_code: string;
  discount_codes: string[];
  metadata: {
    vendor: string;
    source_code: string;
  };
}

// Ritual cart data interface
export interface RitualCartData {
  products: Array<{
    planId: string;
    quantity: number;
    variantId?: string;
    productId?: string;
  }>;
  discountCodes: string[];
  attribution: {
    vendor: string;
    sourceCode: string;
    checkoutId: string;
  };
}

/**
 * Read the Fermat cart cookie from the browser
 * @returns FermatCheckout object or null if cookie not found/invalid
 */
export function readFermatCookie(): FermatCheckout | null {
  try {
    const cookieValue = getCookie('fermat_cart');
    if (!cookieValue) {
      // No Fermat cookie found
      return null;
    }
    
    const decodedData = decodeFermatCookie(cookieValue);
    if (!decodedData) {
      // Failed to decode Fermat cookie
      return null;
    }
    
    if (!validateFermatData(decodedData)) {
      // Invalid Fermat data structure
      return null;
    }
    
    return decodedData;
  } catch (error) {
    // Error reading Fermat cookie
    return null;
  }
}

/**
 * Decode Base64-encoded Fermat cookie value
 * @param cookieValue - The raw cookie value from browser
 * @returns FermatCheckout object or null if decoding fails
 */
function decodeFermatCookie(cookieValue: string): FermatCheckout | null {
  try {
    // Decode Base64 to string
    const decodedString = atob(cookieValue);
    
    // Parse JSON
    const parsedData = JSON.parse(decodedString);
    
    return parsedData as FermatCheckout;
  } catch (error) {
    // Error decoding Fermat cookie
    return null;
  }
}

/**
 * Validate Fermat data structure
 * @param data - The data to validate
 * @returns True if valid Fermat checkout data
 */
function validateFermatData(data: any): data is FermatCheckout {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check required fields
  if (!data.id || typeof data.id !== 'string') {
    return false;
  }

  if (!data.items || !Array.isArray(data.items) || data.items.length === 0) {
    return false;
  }

  // Validate items
  for (const item of data.items) {
    if (!item || typeof item !== 'object') {
      return false;
    }

    if (!item.external_price_id || typeof item.external_price_id !== 'string') {
      return false;
    }

    if (!item.quantity || typeof item.quantity !== 'number' || item.quantity <= 0) {
      return false;
    }
  }

  // Check metadata
  if (!data.metadata || typeof data.metadata !== 'object') {
    return false;
  }

  if (!data.metadata.vendor || typeof data.metadata.vendor !== 'string') {
    return false;
  }

  if (!data.metadata.source_code || typeof data.metadata.source_code !== 'string') {
    return false;
  }

  return true;
}

/**
 * Map Fermat checkout data to Ritual cart format
 * @param fermatData - The Fermat checkout data
 * @returns Ritual cart data structure
 */
export function mapFermatToRitual(fermatData: FermatCheckout): RitualCartData {
  const products = fermatData.items.map(item => ({
    planId: item.external_price_id,
    quantity: item.quantity,
    variantId: item.variant_id,
    productId: item.product_id,
  }));

  const discountCodes = fermatData.discount_codes || [];

  const attribution = {
    vendor: fermatData.metadata.vendor,
    sourceCode: fermatData.metadata.source_code,
    checkoutId: fermatData.id,
  };

  return {
    products,
    discountCodes,
    attribution,
  };
}
